import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  ArrowUpRight,
  Bot,
  ClipboardList,
  Eye,
  Grid2x2,
  Lightbulb,
  Search,
  ShieldCheck,
  Star,
  TrendingDown,
  Trophy,
  Users,
} from "lucide-react";

/**
 * The in-app facilitator's guide: how a session goes from a wall of sticky
 * notes to a ranked list of things the company will actually do. Static
 * content on purpose — it should read the same for everyone, offline-safe.
 */
export function HowToTab() {
  return (
    <div className="max-w-3xl space-y-6">
      <p className="text-sm text-muted-foreground">
        This tool takes a room from &ldquo;lots of opinions&rdquo; to &ldquo;a ranked,
        owned list of what we&apos;ll work on.&rdquo; Two exercises feed each other —
        but you don&apos;t have to do both:{" "}
        <span className="font-medium text-foreground">
          if you already know your candidate initiatives, skip the SWOT and jump
          straight to Impact vs Effort.
        </span>
      </p>

      <Step
        n={1}
        icon={Users}
        title="Set up the room"
      >
        Invite everyone from the participants button in the header — each person
        gets a color, so you always know whose note is whose. Invited people see
        only the sessions they&apos;re part of. The AI chip shows which local model
        assists this session; the default is picked for speed.
      </Step>

      <Step n={2} icon={ShieldCheck} title="SWOT: collect honestly (optional)">
        Everyone writes drafts on their own board-side panel — drafts are private
        until posted, so nobody anchors on the loudest voice. Post with the
        button for an AI gut-check (&ldquo;is this threat really a
        weakness?&rdquo; — you always decide), drag a draft straight onto a lane,
        or use <span className="font-medium">Post all</span> and let the AI flag
        doubtful ones afterward with an amber badge.
        <div className="mt-2 grid gap-1.5 text-xs text-muted-foreground sm:grid-cols-2">
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0" /> Strengths → what to{" "}
            <b>continue</b> doing
          </span>
          <span className="flex items-center gap-1.5">
            <TrendingDown className="h-3.5 w-3.5 shrink-0" /> Weaknesses → what to{" "}
            <b>stop</b> or fix
          </span>
          <span className="flex items-center gap-1.5">
            <Lightbulb className="h-3.5 w-3.5 shrink-0" /> Opportunities → what to{" "}
            <b>start</b>
          </span>
          <span className="flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Threats → what to
            watch and mitigate
          </span>
        </div>
      </Step>

      <Step n={3} icon={Bot} title="Group into themes">
        Drag notes into lanes, or hit <span className="font-medium">AI categorize</span>{" "}
        to sort the uncategorized ones into themes. Write an AI summary per lane
        from the lane menu — the summary is what travels forward. Talk through
        the flagged and ambiguous notes as a group; that discussion is the point.
      </Step>

      <Step n={4} icon={ClipboardList} title="SWOT Summary: pick what carries forward">
        Lanes rank by weight (their share of the aspect&apos;s notes). Check the
        themes that deserve action and create initiatives from them in one go —
        each keeps a link back to the SWOT theme it came from.
      </Step>

      <Step n={5} icon={Grid2x2} title="Impact vs Effort: score it blind">
        Add initiatives directly or use the promoted themes. On each one, set the
        company-impact color, tag the departments that would carry the work, and
        link the annual company goals it would move. Everyone scores impact and
        effort 1–10 — scores stay hidden until the facilitator hits{" "}
        <span className="inline-flex items-center gap-1">
          <Eye className="h-3.5 w-3.5" /> Reveal
        </span>
        , then notes place themselves by the room&apos;s averages (5,5 is the
        center). Drag to override — the ✋ marker shows placement diverging from
        scores. Top-left is your &ldquo;do these&rdquo; quadrant; top-right are
        the big bets.
      </Step>

      <Step n={6} icon={Trophy} title="Summary: decide and commit">
        The ranked takeaways, split company-wide (3+ departments tagged) versus
        department-specific. Star{" "}
        <span className="inline-flex items-center gap-1">
          <Star className="h-3.5 w-3.5 fill-warning text-warning" /> Select
        </span>{" "}
        the focus items, then{" "}
        <span className="inline-flex items-center gap-1">
          <ArrowUpRight className="h-3.5 w-3.5" /> create objectives
        </span>{" "}
        from them — that hands each one to the Objectives tracker with a quarter,
        an owner, and its goal link, where milestones and team responsibilities
        get built out.
      </Step>

      <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
        <span className="mb-1 flex items-center gap-1.5 font-medium text-foreground">
          <Search className="h-3.5 w-3.5" /> Tips
        </span>
        Ctrl/Cmd+K searches every note, lane, and summary in the session. Grid
        cards collapse to chips — tap to expand, drag either form. Hold a card
        near any edge of the wall and it scrolls for you.
      </div>
    </div>
  );
}

function Step({
  n,
  icon: Icon,
  title,
  children,
}: {
  n: number;
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand/10 font-semibold text-brand">
        {n}
      </span>
      <div className="min-w-0 space-y-1">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {title}
        </h2>
        <div className="text-sm text-muted-foreground">{children}</div>
      </div>
    </section>
  );
}
